import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { Divider, Select, Row, Col, Tabs, Typography } from 'antd';
import { useScreenWidth, useScreenHeight } from '../../../hooks/useScreen';
import {cumsum} from "../../../utils/math";
import RadialPercentileChart   from "./RadialPercentileChart";
import UncertaintyViolins from "./UncertaintyViolins";
const { Option } = Select;

const calculateStdDev = (data) => {
  if (!data || data.length === 0) return 0;

  const mean = data.reduce((sum, val) => sum + val, 0) / data.length;
  const squareDiffs = data.map(val => Math.pow(val - mean, 2));
  const avgSquareDiff = squareDiffs.reduce((sum, val) => sum + val, 0) / data.length;
  return Math.sqrt(avgSquareDiff);
};

const PercentileChart = ({ assetNames, simulations, chartHeight }) => {
  // Limit the assetNames to 4 assets for this chart
  const limitedAssetNames = assetNames.slice(0, 4);
  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return simulations.map(sim => sim[assetIndex]);
  };
  const screenH = useScreenHeight();

  const viridisColors = [
    '#440154', '#48186a', '#472d7b', '#424086', '#3b528b', '#33638d', '#2c728e', '#26828e',
    '#21918c', '#1fa088', '#28ae80', '#3fbc73', '#5ec962', '#84d44b', '#addc30', '#d8e219', '#fde725'
  ];

  const percentiles = [5, 25, 50, 75, 95];
  const traces = limitedAssetNames.map((asset, assetIndex) => {
    const assetData = getAssetData(asset);
    return percentiles.map((percentile, index) => ({
      y: assetData.map((_, i) => {
        const sortedData = assetData.slice(0, i + 1).sort((a, b) => a - b);
        const percentileIndex = Math.floor(sortedData.length * percentile / 100);
        return sortedData[percentileIndex];
      }),
      type: 'line',
      name: `${asset} (${percentile}th percentile)`,
      line: {
        color: viridisColors[assetIndex % viridisColors.length],
        dash: index === 2 ? 'solid' : 'dash',
        width: index === 2 ? 3 : 2,
      },
    }));
  }).flat();

  const layout = {
    autosize: true,
    height: chartHeight,
    margin: { l: 50, r: 50, b: 50, t: 30, pad: 4 },
    xaxis: { title: 'Simulation Step' },
    yaxis: { title: 'Value' },
  };

  return <Plot data={traces} layout={layout} useResizeHandler={true} style={{ width: '100%', height: screenH*0.5 }} />;
};

const ProbabilityDistributionChart = ({ assetNames, simulations, chartHeight }) => {
  // Limit the assetNames to 4 assets for this chart
  const limitedAssetNames = assetNames.slice(0, 4);
  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return simulations.map(sim => sim[assetIndex]);
  };
  const screenH = useScreenHeight();

  // Custom colors for better visibility of lines
  const lineColors = [
    '#440154', // deep purple
    '#21918c', // teal
    '#3fbc73', // green
    '#fde725'  // yellow
  ];

  const calculateKDE = (data, bandwidth = 'silverman') => {
    // Sort the data for proper distribution
    const sortedData = [...data].sort((a, b) => a - b);

    // Generate points for smooth line
    const points = 200;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min;
    const x = Array.from({length: points}, (_, i) => min + (range * i) / (points - 1));

    // Simple kernel density estimation
    const y = x.map(xi => {
      return data.reduce((sum, di) => {
        const z = (xi - di) / bandwidth;
        return sum + Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
      }, 0) / (data.length * bandwidth);
    });

    return { x, y };
  };

  const traces = limitedAssetNames.map((asset, index) => {
    const assetData = simulations.map(sim => sim[assetNames.indexOf(asset)]);
    const { x, y } = calculateKDE(assetData, calculateStdDev(assetData) * 0.75);

    return {
      x: x,
      y: y,
      type: 'scatter',
      mode: 'lines',
      name: asset,
      line: {
        color: lineColors[index],
        width: 2
      },
      hoverinfo: 'x+y+name'
    };
  });

  const layout = {
    autosize: true,
    height: chartHeight,
    margin: { l: 50, r: 50, b: 50, t: 30, pad: 4 },
    xaxis: {
      title: 'Value',
      showgrid: true,
      gridcolor: '#E0E0E0'
    },
    yaxis: {
      title: 'Density',
      showgrid: true,
      gridcolor: '#E0E0E0'
    },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    showlegend: true,
    legend: {
      x: 0.02,
      y: 0.98,
      bgcolor: 'rgba(255,255,255,0.9)'
    }
  };

  return (
    <Plot
      data={traces}
      layout={layout}
      useResizeHandler={true}
      style={{ width: '100%', height: screenH * 0.5 }}
      config={{ responsive: true }}
    />
  );
};


// LineChart Component
const LineChart = ({ assetNames, simulations, chartHeight }) => {
  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return cumsum(simulations.map(sim => sim[assetIndex]));
  };

  const viridisColors = [
    '#440154', '#48186a', '#472d7b', '#424086', '#3b528b', '#33638d', '#2c728e', '#26828e',
    '#21918c', '#1fa088', '#28ae80', '#3fbc73', '#5ec962', '#84d44b', '#addc30', '#d8e219', '#fde725'
  ];

  const traces = assetNames.map((asset, index) => ({
    y: getAssetData(asset),
    type: 'line',
    name: asset,
    line: { color: viridisColors[index % viridisColors.length] },
  }));

  const layout = {
    autosize: true,
    height: chartHeight,
    margin: { l: 50, r: 50, b: 50, t: 30, pad: 4 },
    xaxis: { title: 'Simulation Step', zerolinecolor: '#fff' },
    yaxis: { title: 'Value', zerolinecolor: '#fff'  },
  };

  return <Plot data={traces} layout={layout} useResizeHandler={true} style={{ width: '100%', height: '100%' }} />;
};


const AssetSelector = ({
  assetNames,
  selectedAssets,
  onChange,
  selectorCount = 1,
  labels = ['Asset 1', 'Asset 2']
}) => (
  <Row gutter={[16, 16]} className="mb-4 px-6" justify={'center'}>
    {Array.from({ length: selectorCount }).map((_, index) => (
      <Col span={24 / selectorCount} key={index}>
        <Select
          className="w-full"
          value={selectedAssets[`asset${index + 1}`]}
          onChange={(value) => {
            const newAssets = { ...selectedAssets };
            newAssets[`asset${index + 1}`] = value;
            onChange(newAssets);
          }}
          style={{maxWidth:200}}
          placeholder={labels[index]}
        >
          {assetNames.map((name) => (
            <Select.Option key={name} value={name}>{name?.toUpperCase()}</Select.Option>
          ))}
        </Select>
      </Col>
    ))}
  </Row>
);

const ChartSection = ({
  title,
  description,
  chart,
    minColumn=12,
    bordered=false,
  selectorConfig = null, // { count: number, onChange: function, selected: object }
}) => (

      <Col xs={24} sm={24} md={24} lg={24} xl={minColumn} xxl={minColumn} style={{flexDirection: 'column'}}>
        <div className={`round-2 ${bordered && 'bordered'}`} style={{flexGrow: '1'}}>
          <div className={'py-4 px-6'}>
            <Typography.Title level={5} style={{textTransform: 'uppercase'}}>{title}</Typography.Title>
            <Typography.Paragraph className="text-gray-600 my-4">
              {description}
            </Typography.Paragraph>
            {selectorConfig && (
              <AssetSelector
                assetNames={selectorConfig.assetNames}
                selectedAssets={selectorConfig.selected}
                onChange={selectorConfig.onChange}
                selectorCount={selectorConfig.count}
                labels={selectorConfig.labels}
              />
            )}
          </div>
          <div className={'p-2'}>
            {chart}
          </div>
        </div>

      </Col>
);


const BoxChart = ({ assetNames, simulations, chartHeight }) => {
  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return simulations.map(sim => sim[assetIndex]);
  };

  const viridisColors = [
    '#440154', '#48186a', '#472d7b', '#424086', '#3b528b', '#33638d', '#2c728e', '#26828e',
    '#21918c', '#1fa088', '#28ae80', '#3fbc73', '#5ec962', '#84d44b', '#addc30', '#d8e219', '#fde725'
  ];

  const traces = assetNames.map((asset, index) => ({
    y: getAssetData(asset),
    type: 'box',
    name: asset,
    boxpoints: 'outliers',
    marker: {
      color: viridisColors[index % viridisColors.length],
      line: { width: 0 }  // Set line width to 0 to remove the border
    }
  }));

  const layout = {
    autosize: true,
    height: chartHeight,
    margin: { l: 50, r: 50, b: 50, t: 30, pad: 4 },
    yaxis: { title: 'Return',zerolinecolor: '#fff'  },
    xaxis: {zerolinecolor: '#fff' }
  };
  const screenH = useScreenHeight();

  return <Plot data={traces} layout={layout} useResizeHandler={true} style={{ width: '100%', height: screenH*0.5 }} />;
};



// Updated JointDistributionPlot to receive selections as props
const JointDistributionPlot = ({
  assetNames,
  simulations,
  selectedAsset1,
  selectedAsset2
}) => {
  const colors = {
    asset1: 'rgba(132, 212, 75, 0.7)',
    asset2: 'rgba(75, 192, 192, 0.7)',
    scatter: 'rgba(102, 126, 234, 0.6)'
  };

  const getAssetData = (assetName) => {
    const assetIndex = assetNames.indexOf(assetName);
    return simulations.map((sim) => sim[assetIndex]);
  };

  const asset1Data = getAssetData(selectedAsset1);
  const asset2Data = getAssetData(selectedAsset2);

  const traces = [
    {
      type: 'scatter',
      mode: 'markers',
      x: asset1Data,
      y: asset2Data,
      name: 'Joint Distribution',
      marker: {
        color: colors.scatter,
        size: 4
      },
      xaxis: 'x',
      yaxis: 'y',
    },
    {
      type: 'histogram',
      x: asset1Data,
      name: selectedAsset1,
      marker: { color: colors.asset1 },
      yaxis: 'y2',
    },
    {
      type: 'histogram',
      y: asset2Data,
      name: selectedAsset2,
      marker: { color: colors.asset2 },
      xaxis: 'x2',
    }
  ];

  const layout = {
    autosize: true,
    height: 500,
    showlegend: false,
    grid: {
      rows: 2,
      columns: 2,
      pattern: 'independent',
      roworder: 'top to bottom'
    },
    xaxis: {
      title: selectedAsset1,
      domain: [0, 0.85],
      showgrid: true,
      showline: false,
      zeroline: false,
      zerolinecolor: 'white'
    },
    yaxis: {
      title: selectedAsset2,
      domain: [0, 0.85],
      showgrid: true,
      zerolinecolor: 'white'
    },
    xaxis2: {
      domain: [0.85, 1],
      showgrid: false,
      zerolinecolor: 'white'
    },
    yaxis2: {
      domain: [0.85, 1],
      showgrid: false,
      zerolinecolor: 'white'
    },
    margin: {
      l: 50,
      r: 20,
      b: 50,
      t: 20,
      pad: 0
    },
  };
  const screenH = useScreenHeight();

  return (
    <Plot
      data={traces}
      layout={layout}
      config={{ responsive: true, displayModeBar: false }}
      useResizeHandler={true}
      style={{ width: '100%', height: screenH*0.5 }}
    />
  );
};

const SimulationView = ({ simulations, assetNames }) => {
  const [chartHeight, setChartHeight] = useState(400);
  const width = useScreenWidth();
  const [selectedAssets, setSelectedAssets] = useState({
    asset1: assetNames[0],
    asset2: assetNames[1] || assetNames[0]
  });
  const [selectedOutcomeAssets, setSelectedOutcomeAssets] = useState({
    asset1: assetNames[0],
    asset2: assetNames[1] || assetNames[0],
    asset3: assetNames[2] || assetNames[0],
    asset4: assetNames[3] || assetNames[0]
  });

  useEffect(() => {
    const newHeight = width < 1200 ? 300 : 400;
    setChartHeight(newHeight);
  }, [width]);

  // Helper to get selected asset names array
  const getSelectedAssetNames = () => {
    return Object.values(selectedOutcomeAssets);
  };

  useEffect(() => {
    const newHeight = width < 1200 ? 300 : 400;
    setChartHeight(newHeight);
  }, [width]);

  return (
    <Tabs tabPosition={'left'}>
      <Tabs.TabPane tab="Sims" key="1">
        <div className={'p-6'}>

          <Row gutter={[32, 32]}>
            <ChartSection
              title="Joint Distribution Analysis"
              bordered={false}
              description="Visualize how pairs of assets move together in the simulations. The scatter pattern reveals correlation strength and direction, while density shows the likelihood of joint outcomes."
              chart={
                <JointDistributionPlot
                  assetNames={assetNames}
                  simulations={simulations}
                  selectedAsset1={selectedAssets.asset1}
                  selectedAsset2={selectedAssets.asset2}
                />
              }
              selectorConfig={{
                count: 2,
                assetNames,
                selected: selectedAssets,
                onChange: setSelectedAssets,
                labels: ['Primary Asset', 'Secondary Asset']
              }}
            />
            <ChartSection
                bordered={false}
              title="Probability Distribution Lines"
              description="This chart displays the probability distribution of each asset using histograms, highlighting the frequency of different return values."
              chart={
                <ProbabilityDistributionChart
                  assetNames={getSelectedAssetNames()}
                  simulations={simulations}
                  chartHeight={chartHeight}
                />
              }
              selectorConfig={{
                count: 4,
                assetNames,
                selected: selectedOutcomeAssets,
                onChange: setSelectedOutcomeAssets,
                labels: ['Asset 1', 'Asset 2', 'Asset 3', 'Asset 4']
              }}
            />
            <Divider style={{borderColor: "#ededed"}}/>
            <ChartSection
              title="Return Distribution Ranges"
              description="Box plots show the key statistics of return distributions for each asset: median, quartiles, and outliers. Compare the spread and central tendencies across assets."
              chart={<BoxChart assetNames={assetNames} simulations={simulations} chartHeight={chartHeight} />}
            />

          </Row>
        </div>
      </Tabs.TabPane>

      <Tabs.TabPane tab="Outcomes" key="2">
        <div className={'p-6'}>
          <ChartSection
              title="Cumulative Value Paths"
              description="Track how asset values evolve over time in the simulations. The lines show different potential paths, helping visualize the range of possible outcomes and growth patterns."
              chart={<LineChart assetNames={assetNames} simulations={simulations} chartHeight={chartHeight} />}
              minColumn={24}
            />
          <ChartSection
            title="Percentile Analysis"
            description="View the evolution of key percentiles (5th, 25th, 50th, 75th, 95th) over time for each asset. The solid line shows the median path, while dashed lines show the range of likely outcomes."
            chart={<UncertaintyViolins assetNames={assetNames} simulations={simulations} chartHeight={chartHeight} />}
            reversed={true}
            minColumn={24}
          />

        </div>
      </Tabs.TabPane>
    </Tabs>
  );
};


export default SimulationView;